import React, { useEffect, useState } from "react";
import {
  Modal, Form, Input, InputNumber, DatePicker, Select, Row, Col, Button, Tag, Switch,
} from "antd";
import { LockOutlined, CalendarOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import { BASE_API_URL } from "../../../../utils/consts";
import type { VenueEvent } from "../../../../utils/api/venueEventsAPI";
import type { FloorPlanTemplate } from "../../../../utils/api/floorPlanTemplatesAPI";
import { getFloorPlanTemplates } from "../../../../utils/api/floorPlanTemplatesAPI";
import "./AddVenueEventModal.scss";

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
});

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (values: Partial<VenueEvent>) => Promise<void>;
  editData?: VenueEvent | null;
  loading?: boolean;
  restrictedEdit?: boolean;
  canEditName?: boolean;
  canEditCapacity?: boolean;
  canEditTables?: boolean;
};

const AddVenueEventModal: React.FC<Props> = ({
  open, onClose, onSave, editData, loading,
  restrictedEdit, canEditName, canEditCapacity, canEditTables,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isEdit = !!editData;
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId: string = typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const [employees, setEmployees] = useState<{ _id: string; firstName: string; lastName: string }[]>([]);
  const [templates, setTemplates] = useState<FloorPlanTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    axios.get(`${BASE_API_URL}/${clientId}/users`).then((res: any) => {
      setEmployees(res.data || []);
    }).catch(() => {});
    getFloorPlanTemplates(clientId).then((res) => setTemplates(res.data)).catch(() => {});
  }, [clientId]);

  useEffect(() => {
    if (open) {
      if (editData) {
        const responsibleId = editData.responsibleUserId
          ? (typeof editData.responsibleUserId === "object" ? (editData.responsibleUserId as any)._id : editData.responsibleUserId)
          : undefined;
        const kitchenId = editData.kitchenUserId
          ? (typeof editData.kitchenUserId === "object" ? (editData.kitchenUserId as any)._id : editData.kitchenUserId)
          : undefined;
        setIsLocked(!!editData.isLocked);
        form.setFieldsValue({
          title: editData.title,
          description: editData.description || "",
          type: editData.type,
          date: dayjs(editData.date),
          time: editData.time,
          endTime: editData.endTime || undefined,
          totalCapacity: editData.totalCapacity,
          totalTables: editData.totalTables ?? undefined,
          minAdvanceBookingDays: editData.minAdvanceBookingDays ?? undefined,
          ticketPrice: editData.ticketPrice,
          currency: editData.currency,
          status: editData.status,
          responsibleUserId: responsibleId,
          kitchenUserId: kitchenId,
          lockedGuestCount: editData.lockedGuestCount ?? undefined,
          lockedPrice: editData.lockedPrice ?? undefined,
          lockedPriceType: editData.lockedPriceType ?? undefined,
        });
      } else {
        form.resetFields();
        setSelectedTemplateId(null);
        setIsLocked(false);
        form.setFieldsValue({
          date: dayjs(),
          type: "other",
          status: "published",
          ticketPrice: 0,
          currency: "EUR",
          totalCapacity: 50,
        });
      }
    }
  }, [open, editData]);

  const onFinish = async (values: any) => {
    if (restrictedEdit) {
      const payload: Partial<VenueEvent> = {};
      if (canEditName) payload.title = values.title;
      if (canEditCapacity) payload.totalCapacity = values.totalCapacity;
      if (canEditTables) payload.totalTables = values.totalTables;
      await onSave(payload);
      return;
    }
    const selectedTemplate = selectedTemplateId
      ? templates.find((tpl) => tpl._id === selectedTemplateId)
      : null;
    await onSave({
      ...values,
      date: values.date ? values.date.format("YYYY-MM-DD") : undefined,
      isLocked,
      lockedGuestCount: isLocked ? values.lockedGuestCount : null,
      lockedPrice: isLocked ? (values.lockedPrice ?? null) : null,
      lockedPriceType: isLocked ? (values.lockedPriceType ?? null) : null,
      ...(selectedTemplate ? { tableLayout: selectedTemplate.layout } : {}),
    });
    form.resetFields();
    setSelectedTemplateId(null);
    setIsLocked(false);
  };

  const modalTitle = (
    <div className="aem-title">
      <CalendarOutlined />
      <span>
        {restrictedEdit
          ? t("Edit Event")
          : isEdit
          ? t("Edit Event")
          : t("New Event")}
      </span>
    </div>
  );

  if (restrictedEdit) {
    return (
      <Modal
        title={modalTitle}
        open={open}
        onCancel={onClose}
        footer={null}
        width={420}
        destroyOnClose
        className="add-venue-event-modal"
      >
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false} className="aem-form">
          <Form.Item name="title" label={t("Event Title")}>
            <Input size="large" disabled={!canEditName} />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="totalCapacity"
                label={t("Capacity")}
                rules={canEditCapacity ? [{ required: true, message: t("Required") }] : []}
              >
                <InputNumber min={1} size="large" style={{ width: "100%" }} disabled={!canEditCapacity} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="totalTables" label={t("Number of Tables")}>
                <InputNumber min={1} size="large" style={{ width: "100%" }} placeholder={t("e.g. 20")} disabled={!canEditTables} />
              </Form.Item>
            </Col>
          </Row>

          <div className="aem-footer">
            <Button size="large" onClick={onClose}>{t("Cancel")}</Button>
            <Button type="primary" size="large" htmlType="submit" loading={loading}>
              {t("Save Changes")}
            </Button>
          </div>
        </Form>
      </Modal>
    );
  }

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnClose
      className="add-venue-event-modal"
    >
      <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false} className="aem-form">
        <Form.Item
          name="title"
          label={t("Event Title")}
          rules={[{ required: true, message: t("Required") }]}
        >
          <Input placeholder={t("e.g. Jazz Night, Stand-up Comedy")} size="large" />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="type" label={t("Type")}>
              <Select size="large">
                <Select.Option value="concert">{t("Concert")}</Select.Option>
                <Select.Option value="standup">{t("Stand-up")}</Select.Option>
                <Select.Option value="band">{t("Live Band")}</Select.Option>
                <Select.Option value="private">{t("Private Event")}</Select.Option>
                <Select.Option value="sports">{t("Sports")}</Select.Option>
                <Select.Option value="other">{t("Other")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="status" label={t("Status")}>
              <Select size="large">
                <Select.Option value="draft">{t("Draft")}</Select.Option>
                <Select.Option value="published">{t("Published")}</Select.Option>
                <Select.Option value="cancelled">{t("Cancelled")}</Select.Option>
                <Select.Option value="completed">{t("Completed")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="date"
              label={t("Date")}
              rules={[{ required: true, message: t("Required") }]}
            >
              <DatePicker size="large" style={{ width: "100%" }} format="DD.MM.YYYY" allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="time"
              label={t("Start Time")}
              rules={[{ required: true, message: t("Required") }]}
            >
              <Select size="large" placeholder={t("Select time")} showSearch>
                {TIME_OPTIONS.map((opt) => (
                  <Select.Option key={opt} value={opt}>{opt}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="endTime" label={t("End Time")}>
              <Select size="large" placeholder={t("Optional")} showSearch allowClear>
                {TIME_OPTIONS.map((opt) => (
                  <Select.Option key={opt} value={opt}>{opt}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className={`aem-locked-banner${isLocked ? " aem-locked-banner--active" : ""}`}>
          <LockOutlined className="aem-locked-banner__icon" />
          <span className="aem-locked-banner__label">
            {t("Locked event (guest count pre-filled, no reservations)")}
          </span>
          <Switch
            checked={isLocked}
            onChange={(val) => {
              setIsLocked(val);
              if (!val) form.setFieldValue("lockedGuestCount", undefined);
            }}
          />
        </div>

        {isLocked ? (
          <>
            <Form.Item
              name="lockedGuestCount"
              label={t("Number of guests")}
              rules={[{ required: true, message: t("Required") }]}
            >
              <InputNumber min={1} size="large" style={{ width: "100%" }} placeholder={t("e.g. 150")} />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} sm={8}>
                <Form.Item name="lockedPrice" label={t("Price (optional)")}>
                  <InputNumber min={0} size="large" style={{ width: "100%" }} placeholder={t("e.g. 500")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="lockedPriceType" label={t("Price type")}>
                  <Select size="large" placeholder={t("Select")} allowClear>
                    <Select.Option value="per_person">{t("Per person")}</Select.Option>
                    <Select.Option value="flat">{t("Flat (total)")}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="currency" label={t("Currency")}>
                  <Select size="large">
                    <Select.Option value="EUR">EUR</Select.Option>
                    <Select.Option value="BAM">BAM</Select.Option>
                    <Select.Option value="RSD">RSD</Select.Option>
                    <Select.Option value="USD">USD</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </>
        ) : (
          <Row gutter={12}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="totalCapacity"
                label={t("Capacity")}
                rules={[{ required: true, message: t("Required") }]}
              >
                <InputNumber min={1} size="large" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="ticketPrice" label={t("Ticket Price")}>
                <InputNumber min={0} size="large" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="currency" label={t("Currency")}>
                <Select size="large">
                  <Select.Option value="EUR">EUR</Select.Option>
                  <Select.Option value="BAM">BAM</Select.Option>
                  <Select.Option value="RSD">RSD</Select.Option>
                  <Select.Option value="USD">USD</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="totalTables" label={t("Number of Tables (optional)")}>
              <InputNumber min={1} size="large" style={{ width: "100%" }} placeholder={t("e.g. 20")} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="minAdvanceBookingDays" label={t("Min. days to book in advance")}>
              <InputNumber min={0} size="large" style={{ width: "100%" }} placeholder={t("e.g. 2")} />
            </Form.Item>
          </Col>
        </Row>

        {!isEdit && templates.length > 0 && (
          <Form.Item label={t("Floor Plan Template (optional)")}>
            <Select
              size="large"
              allowClear
              placeholder={t("Select a saved layout template")}
              value={selectedTemplateId || undefined}
              onChange={(val) => setSelectedTemplateId(val || null)}
              optionLabelProp="label"
            >
              {templates.map((tpl) => (
                <Select.Option key={tpl._id} value={tpl._id} label={tpl.name}>
                  <span>{tpl.name}</span>
                  <Tag style={{ marginLeft: 8, fontSize: 11 }}>{tpl.tableCount} {t("tables")}</Tag>
                </Select.Option>
              ))}
            </Select>
            {selectedTemplateId && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                {t("The selected layout will be applied when the event is created.")}
              </div>
            )}
          </Form.Item>
        )}

        <Form.Item name="description" label={t("Description")}>
          <Input.TextArea rows={2} placeholder={t("Short description (optional)")} maxLength={500} showCount />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="responsibleUserId"
              label={t("Responsible Person")}
              rules={[{ required: true, message: t("Required") }]}
            >
              <Select
                size="large"
                placeholder={t("Select responsible person")}
                showSearch
                optionFilterProp="label"
                options={employees.map((emp) => ({
                  value: emp._id,
                  label: `${emp.firstName} ${emp.lastName}`,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="kitchenUserId"
              label={t("Kitchen Responsible")}
              rules={[{ required: true, message: t("Required") }]}
            >
              <Select
                size="large"
                placeholder={t("Select kitchen person")}
                showSearch
                optionFilterProp="label"
                options={employees.map((emp) => ({
                  value: emp._id,
                  label: `${emp.firstName} ${emp.lastName}`,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <div className="aem-footer">
          <Button size="large" onClick={onClose}>{t("Cancel")}</Button>
          <Button type="primary" size="large" htmlType="submit" loading={loading}>
            {isEdit ? t("Save Changes") : t("Create Event")}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddVenueEventModal;
